import React, { useState, useEffect } from 'react';
import api from '../../utils/axios';
import dayjs from 'dayjs';
import { useNavigate, useLocation } from 'react-router-dom';

const CategoriesPage = () => {
  const [categories, setCategories] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [incomes, setIncomes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const navigate = useNavigate();
  const location = useLocation();
  
  // 从上一个页面获取选择的月份，如果没有则使用当前月份
  const selectedYear = location.state?.currentYear ?? dayjs().year();
  const selectedMonth = location.state?.currentMonth ?? dayjs().month();

  // 初始化数据
  useEffect(() => {
    fetchData();
  }, []);

  // 获取数据
  const fetchData = async () => {
    setLoading(true);
    try {
      // 获取分类数据
      const categoriesResponse = await api.get('/expenses/categories');
      if (categoriesResponse.data && categoriesResponse.data.success) {
        setCategories(categoriesResponse.data.data || []);
      }

      // 获取记账数据，设置较大的limit确保获取所有数据
      const expensesResponse = await api.get('/expenses', {
        params: {
          limit: 1000
        }
      });
      if (expensesResponse.data && expensesResponse.data.success) {
        const data = expensesResponse.data.data.expenses || [];
        
        // 分离收入和支出
        const incomeData = data.filter(item => item.type === 'income');
        const expenseData = data.filter(item => item.type === 'expense');
        
        setIncomes(incomeData);
        setExpenses(expenseData);
      }
    } catch (error) {
      console.error('获取数据失败:', error);
    } finally {
      setLoading(false);
    }
  };

  // 计算分类总金额（选定月份）
  const getCategoryTotal = (categoryId) => {
    const total = [...incomes, ...expenses]
      .filter(item => {
        const itemDate = dayjs(item.date);
        return item.category_id === categoryId && 
               itemDate.month() === selectedMonth && 
               itemDate.year() === selectedYear;
      })
      .reduce((sum, item) => sum + (typeof item.amount === 'number' ? item.amount : parseFloat(item.amount) || 0), 0);
    return total;
  };

  // 获取分类选定月份记录
  const getCategoryMonthlyRecords = (categoryId) => {
    return [...incomes, ...expenses]
      .filter(item => {
        const itemDate = dayjs(item.date);
        return item.category_id === categoryId && 
               itemDate.month() === selectedMonth && 
               itemDate.year() === selectedYear;
      })
      .sort((a, b) => new Date(b.date) - new Date(a.date));
  };

  // 切换分类展开状态
  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  // 返回上一页
  const handleBack = () => {
    navigate('/mobile/expenses');
  };

  // 排序分类
  const sortCategories = (categories, type) => {
    return categories
      .filter(cat => cat.type === type)
      .sort((a, b) => getCategoryTotal(b.id) - getCategoryTotal(a.id));
  };

  return (
    <div style={{
      width: '100%',
      maxWidth: '100%',
      height: '100vh',
      margin: '0',
      background: '#f5f5f5',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* 顶部导航栏 */}
      <div style={{
        background: 'rgba(255, 218, 87, 1)',
        color: '#333',
        padding: '20px 16px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        <button
          style={{
            background: 'transparent',
            border: 'none',
            color: '#333',
            fontSize: '18px',
            cursor: 'pointer',
          }}
          onClick={handleBack}
        >
          ←
        </button>
        <div style={{ fontSize: '18px', fontWeight: 'bold' }}>所有分类</div>
        <div style={{ width: '24px' }}></div>
      </div>

      {/* 分类列表 */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '10px' }}>
        {/* 支出分类 */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '10px', padding: '0 10px' }}>支出分类</div>
          {sortCategories(categories, 'expense').map(category => {
            const total = getCategoryTotal(category.id);
            const isExpanded = expandedCategories.has(category.id);
            const monthlyRecords = getCategoryMonthlyRecords(category.id);
            
            return (
              <div key={category.id} style={{ marginBottom: '8px' }}>
                {/* 分类标题 */}
                <div 
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '15px',
                    background: '#fff',
                    borderRadius: '8px',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                    cursor: 'pointer'
                  }}
                  onClick={() => toggleCategory(category.id)}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ 
                      width: '40px', 
                      height: '40px', 
                      borderRadius: '20px', 
                      background: category.color || '#1890ff',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      color: '#fff',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      textAlign: 'center',
                      padding: '2px',
                      flexDirection: 'column'
                    }}>
                      {(() => {
                        const displayName = category.name.length > 4 ? category.name.substring(0, 4) : category.name;
                        
                        if (displayName.length === 4) {
                          return (
                            <>
                              <div>{displayName.substring(0, 2)}</div>
                              <div>{displayName.substring(2, 4)}</div>
                            </>
                          );
                        } else if (displayName.length === 3) {
                          return (
                            <>
                              <div>{displayName.substring(0, 1)}</div>
                              <div>{displayName.substring(1, 3)}</div>
                            </>
                          );
                        } else {
                          return displayName;
                        }
                      })()}
                    </div>
                    <div style={{ fontSize: '12px' }}>{category.name}</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ color: '#ff4d4f', fontWeight: 'bold', fontSize: '12px' }}>
                      ¥{total.toFixed(2)}
                    </div>
                    <div style={{ fontSize: '14px' }}>
                      {isExpanded ? '▼' : '▶'}
                    </div>
                  </div>
                </div>
                
                {/* 本月记录 */}
                {isExpanded && monthlyRecords.length > 0 && (
                  <div style={{ 
                    background: '#fff', 
                    borderRadius: '0 0 8px 8px', 
                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                    marginTop: '-8px',
                    padding: '10px'
                  }}>
                    {monthlyRecords.map(record => (
                      <div key={record.id} style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '12px',
                        borderBottom: '1px solid #f0f0f0'
                      }}>
                        <div>
                          <div style={{ fontSize: '12px', color: '#333', marginBottom: '4px' }}>
                            {record.description || category.name}
                          </div>
                          <div style={{ fontSize: '10px', color: '#999' }}>
                            {dayjs(record.date).format('MM-DD HH:mm:ss')}
                          </div>
                        </div>
                        <div style={{ 
                          fontWeight: 'bold', 
                          fontSize: '12px',
                          color: record.type === 'income' ? '#52c41a' : '#ff4d4f'
                        }}>
                          {record.type === 'income' ? '+' : '-'}
                          ¥{parseFloat(record.amount).toFixed(2)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {isExpanded && monthlyRecords.length === 0 && (
                  <div style={{ 
                    background: '#fff', 
                    borderRadius: '0 0 8px 8px', 
                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                    marginTop: '-8px',
                    padding: '20px',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '12px', color: '#999' }}>该月暂无记录</div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* 收入分类 */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '10px', padding: '0 10px' }}>收入分类</div>
          {sortCategories(categories, 'income').map(category => {
            const total = getCategoryTotal(category.id);
            const isExpanded = expandedCategories.has(category.id);
            const monthlyRecords = getCategoryMonthlyRecords(category.id);
            
            return (
              <div key={category.id} style={{ marginBottom: '8px' }}>
                {/* 分类标题 */}
                <div 
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '15px',
                    background: '#fff',
                    borderRadius: '8px',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                    cursor: 'pointer'
                  }}
                  onClick={() => toggleCategory(category.id)}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ 
                      width: '40px', 
                      height: '40px', 
                      borderRadius: '20px', 
                      background: category.color || '#1890ff',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      color: '#fff',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      textAlign: 'center',
                      padding: '2px',
                      flexDirection: 'column'
                    }}>
                      {(() => {
                        const displayName = category.name.length > 4 ? category.name.substring(0, 4) : category.name;
                        
                        if (displayName.length === 4) {
                          return (
                            <>
                              <div>{displayName.substring(0, 2)}</div>
                              <div>{displayName.substring(2, 4)}</div>
                            </>
                          );
                        } else if (displayName.length === 3) {
                          return (
                            <>
                              <div>{displayName.substring(0, 1)}</div>
                              <div>{displayName.substring(1, 3)}</div>
                            </>
                          );
                        } else {
                          return displayName;
                        }
                      })()}
                    </div>
                    <div style={{ fontSize: '12px' }}>{category.name}</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ color: '#52c41a', fontWeight: 'bold', fontSize: '12px' }}>
                      ¥{total.toFixed(2)}
                    </div>
                    <div style={{ fontSize: '14px' }}>
                      {isExpanded ? '▼' : '▶'}
                    </div>
                  </div>
                </div>
                
                {/* 本月记录 */}
                {isExpanded && monthlyRecords.length > 0 && (
                  <div style={{ 
                    background: '#fff', 
                    borderRadius: '0 0 8px 8px', 
                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                    marginTop: '-8px',
                    padding: '10px'
                  }}>
                    {monthlyRecords.map(record => (
                      <div key={record.id} style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '12px',
                        borderBottom: '1px solid #f0f0f0'
                      }}>
                        <div>
                          <div style={{ fontSize: '12px', color: '#333', marginBottom: '4px' }}>
                            {record.description || category.name}
                          </div>
                          <div style={{ fontSize: '10px', color: '#999' }}>
                            {dayjs(record.date).format('MM-DD HH:mm:ss')}
                          </div>
                        </div>
                        <div style={{ 
                          fontWeight: 'bold', 
                          fontSize: '12px',
                          color: record.type === 'income' ? '#52c41a' : '#ff4d4f'
                        }}>
                          {record.type === 'income' ? '+' : '-'}
                          ¥{parseFloat(record.amount).toFixed(2)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {isExpanded && monthlyRecords.length === 0 && (
                  <div style={{ 
                    background: '#fff', 
                    borderRadius: '0 0 8px 8px', 
                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                    marginTop: '-8px',
                    padding: '20px',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '12px', color: '#999' }}>该月暂无记录</div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default CategoriesPage;